import React, { type FC, memo } from 'react';
import { connect, useDispatch } from 'react-redux';
import classNames from 'classnames';
import { List } from 'immutable';
import { compose } from '@reduxjs/toolkit';

import type { BettingContextProps } from '../../types';
import type { ReduxState } from 'app/redux/types';

import { ContextNamespace } from 'app/constants/Context';
import { BET_KEYS_RENDERER_MAP } from 'app/constants/StakeRenderer';
import { withinBettingContext } from 'app/contexts/betting';
import { Icon, IconSize } from 'app/design-system/components/Icon';
import { useTranslation } from 'app/lang';
import { compareStakeProps, mapBetsToStake } from 'app/lib/betting';
import type { RecordOfBetEntity } from 'app/lib/betting/betting-entities';
import type { SportLinesStakeType } from 'app/lib/betting/betting-entities/reducers/matches/utils/match-lines';
import { getLineStakeTypeGroupConf } from 'app/lib/matchGridStakes';

import { Bet, BetBlank } from '../BetRepresentation';

import dictionary from './Stake.dictionary.json';

import styles from './Stake.module.scss';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyType = any;

type StoreProps = {
  stakeType?: number;
  addBet: (matchId: number, betId: number) => void;
  bets: List<RecordOfBetEntity>;
  betKeys: number[];
  disabled: boolean;
  fixedBets: Record<number, boolean>;
  sportLinesStakeType: SportLinesStakeType[];
  conflictedIds: Set<number>;
};

type StakeProps = {
  matchId: number;
  stakeId: number;
  lineId?: number;
  className?: string;
  disabledIconClassName?: string;
};

const Stake: FC<StakeProps & StoreProps> = memo(({
  lineId,
  stakeType,
  addBet,
  bets,
  betKeys,
  disabled,
  fixedBets,
  sportLinesStakeType,
  conflictedIds,
  className,
  disabledIconClassName,
  matchId,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleBetClick = (betId: number) => {
    dispatch(addBet(matchId, betId));
  };

  const isDisabled = disabled && !conflictedIds.size;
  const lineStakeType = sportLinesStakeType.find((line) => (
    line.lineId === lineId && line.stakeType === stakeType
  ));

  const betClassName = (betKeys && betKeys.length === 3) && 'three-bets';

  return (
    <div className={classNames(styles.stake, className, betClassName, { disabled: isDisabled })}
         data-test="stake-container"
         data-test-disabled={disabled}
         data-test-line-group-id={lineStakeType?.groupId}
         title={disabled ? t(dictionary.disabledTitle) : ''}>
      {betKeys && betKeys.map((betKey: number) => {
        /* Bet can not be exist. In this case we displaying the empty cell */
        const bet = bets && bets.find((bet) => (bet.key === betKey));
        const isNotConflictedEntry = bet && conflictedIds.size > 0 && !conflictedIds.has(bet?.id);

        return bet
          ? (
            <Bet key={betKey}
                 bet={bet}
                 labelType="short"
                 priceClassName={styles.betPrice}
                 isInBetSlip={fixedBets[bet.id]}
                 disabled={disabled || !bet.isOpen}
                 conflict={isNotConflictedEntry}
                 onClick={handleBetClick} />
          )
          : <BetBlank key={betKey} />;
      })}

      {isDisabled && (
        <span className={classNames(styles.disabledIcon, disabledIconClassName)}>
          <Icon
              icon="lock"
              size={IconSize.SMALL} />
        </span>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return !compareStakeProps(prevProps, nextProps);
});

const mapStateToProps = (state: ReduxState, {
  stakeId,
  matchId,
  lineId,
  $betting: {
    namespace,
    content: {
      selectors: {
        matches,
      },
    },
    grid: {
      selectors: {
        stakeLayout,
      },
    },
    matchContent,
    matchGrid,
    matchStaking,
    staking,
  },
}: BettingContextProps): StoreProps => {
  const isPrematch = namespace === ContextNamespace.PREMATCH;
  const {
    selectAllConflictedIds,
  } = staking.selectors;
  const addBet = staking?.actions?.addBet;
  const prematchMatchesAddBet = matchStaking?.actions?.addBet;

  const prematchMatches = matchContent?.selectors?.matches;
  const prematchMatchesStakeLayout = matchGrid?.selectors?.stakeLayout;
  const isPrematchMatchSubscribe = (isPrematch && prematchMatches)
                                   && prematchMatches(state)?.get(matchId)?.id === matchId;

  const match = (isPrematch && isPrematchMatchSubscribe && prematchMatches)
    ? prematchMatches(state).get(matchId)
    : matches(state).get(matchId);
  const stake = match?.stakes.get(stakeId);

  const stakeLayoutBetKeys = (isPrematch && isPrematchMatchSubscribe && prematchMatchesStakeLayout)
    ? prematchMatchesStakeLayout(state, { stakeId, matchId })
    : stakeLayout(state, { stakeId, matchId });

  const matchesSelector = (isPrematch && isPrematchMatchSubscribe && prematchMatches)
    ? prematchMatches
    : matches;

  const mappedProps: AnyType = stake
    ? mapBetsToStake(state, matchesSelector, stake, staking)
    : {};

  mappedProps.addBet = (isPrematch && isPrematchMatchSubscribe && prematchMatchesAddBet)
    ? prematchMatchesAddBet
    : addBet;

  mappedProps.lineId = lineId;
  mappedProps.stakeType = stake?.type;
  mappedProps.sportLinesStakeType = getLineStakeTypeGroupConf(match?.sportId);
  mappedProps.betKeys = BET_KEYS_RENDERER_MAP[
    stakeLayoutBetKeys as keyof typeof BET_KEYS_RENDERER_MAP
  ];
  mappedProps.conflictedIds = selectAllConflictedIds(state);

  return mappedProps;
};

export default compose<FC<StakeProps>>(
  withinBettingContext,
  connect(mapStateToProps),
)(Stake);
